import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Plus,
  Search,
  Receipt,
  Download,
  Calendar,
  DollarSign,
  FileStack,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  X,
  Building2,
  Edit2,
  CheckCircle2,
  AlertCircle,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import {
  getAdvanceReceipts,
  AdvanceReceiptApi,
  AdvanceReceiptSummaryApi,
  getAdvanceReceiptSummary,
  downloadAdvanceReceiptPdf,
  postAdvanceReceipt,
} from "../../services/advanceReceiptService";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { useScreenPermission } from "../../hooks/useScreenPermission";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";
import Pagination from "ui/pagination";

interface AdvanceReceipt {
  id: string;
  receiptNo: string;
  date: string;
  tourOperator: string;
  officeName: string;
  itsNumber: string;
  amount: number;
  createdByUserName: string;
  consumedAmount: number;
  paymentMode: string;
  transactionRef: string;
  description: string;
  remarks?: string;
  status: "DRAFT" | "POSTED";
}
interface AdvanceReceiptListPageProps {
  onNavigate: (page: string) => void;
}
export const AdvanceReceiptListPage = ({
  onNavigate,
}: AdvanceReceiptListPageProps) => {
   const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState("");
  const [showStatistics, setShowStatistics] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [summary, setSummary] = useState<AdvanceReceiptSummaryApi | null>(null);
  const [loading, setLoading] = useState(false);
  const [, setLoadingSummary] = useState(false);
  const [receipts, setReceipts] = useState<AdvanceReceipt[]>([]);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromUrl = Number(searchParams.get("page")) || 1;
  const [currentPage, setCurrentPage] = useState(pageFromUrl);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const { canAdd, canEdit, canApprove, screenName, icon: ScreenIcon } = useScreenPermission();

  const [filters, setFilters] = useState({
    receiptNo: "",
    tourOperator: "",
    paymentMode: "all",
    dateFrom: "",
    dateTo: "",
    minAmount: "",
    maxAmount: "",
  });

  useEffect(() => {
    setPageHeader({
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    setCurrentPage(1);
    setSearchParams({ page: "1" });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filters, pageSize]);

  useEffect(() => {
    if (pageFromUrl !== currentPage) {
      setCurrentPage(pageFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageFromUrl]);

  useEffect(() => {
    loadAdvanceReceipts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filters, currentPage]);

  useEffect(() => {
    loadSummary();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 400);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const handlePostReceipt = (receipt: AdvanceReceipt) => {
    const body = {
      id: receipt.id,
      receiptNo: receipt.receiptNo,
    };
    setDraftToPostID(body);
    setDraftDialogOpen(true);
  };

  const confirmDeleteDraft = async () => {
    if (draftToPostID.id) {
      try {
        setLoading(true);
        await postAdvanceReceipt(Number(draftToPostID.id));

        toast.success(`Receipt ${draftToPostID.receiptNo} posted successfully`);
        setReceipts((prev) =>
          prev.map((r) =>
            r.id === draftToPostID.id ? { ...r, status: "POSTED" } : r
          )
        );
      } catch {
        toast.error("Failed to post receipt");
      }  finally {
        setLoading(false);
      }

      setDraftDialogOpen(false);
      setDraftToPostID({ id: null, receiptNo: null });
    }
  };

  const cancelDeleteDraft = () => {
    setDraftDialogOpen(false);
    setDraftToPostID({ id: null, receiptNo: null });
  };

  const [draftDialogOpen, setDraftDialogOpen] = useState(false);
  const [draftToPostID, setDraftToPostID] = useState<{
    id: string | null;
    receiptNo: string | null;
  }>({ id: null, receiptNo: null });

  const mapApiToUI = (api: AdvanceReceiptApi): AdvanceReceipt => ({
    id: String(api.receiptId),
    receiptNo: api.receiptNo,
    date: api.receiptDate,
    tourOperator: api.tourOperatorName,
    officeName: api.officeName,
    itsNumber: api.itsNumber,
    amount: api.amount,
    createdByUserName: api.createdByUserName,
    consumedAmount: 0,
    paymentMode: api.paymentMode,
    transactionRef: api.transactionRef,
    description: api.description,
    remarks: api.remarks,
    status: api.status,
  });

  const loadAdvanceReceipts = async () => {
    try {
      setLoading(true);
      const res = await getAdvanceReceipts({
        searchText: debouncedSearch || null,
        receiptNo: filters.receiptNo || null,
        paymentMode: filters.paymentMode === "all" ? null : filters.paymentMode,
        fromDate: filters.dateFrom || null,
        toDate: filters.dateTo || null,
        minAmount: filters.minAmount ? Number(filters.minAmount) : null,
        maxAmount: filters.maxAmount ? Number(filters.maxAmount) : null,
        pageNo: currentPage,
        pageSize,

        sortBy: "receiptNo",
        sortOrder: "desc",
      });

      if (res.totalCount > 0 && res.items.length === 0 && currentPage > 1) {
        setCurrentPage(1);
        setSearchParams({ page: "1" });
        return;
      }

      setReceipts(res.items.map(mapApiToUI));
      setTotalCount(res.totalCount);
    } catch {
      toast.error("Failed to load advance receipts");
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      setLoadingSummary(true);
      const res = await getAdvanceReceiptSummary();
      setSummary(res);
    } catch {
      toast.error("Failed to load receipt summary");
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleDownloadClick = async (receipt: AdvanceReceipt) => {
    try {
      const blob = await downloadAdvanceReceiptPdf(Number(receipt.id));
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${receipt.receiptNo}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Receipt ${receipt.receiptNo} downloaded successfully`);
    } catch (error) {
      toast.error("Failed to download receipt PDF");
    }
  };

  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn("");
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Apply sorting
  const sortedReceipts = [...receipts].sort((a, b) => {
    if (!sortColumn) return 0;

    let aValue: string | number;
    let bValue: string | number;

    switch (sortColumn) {
      case "receiptNo":
        aValue = a.receiptNo;
        bValue = b.receiptNo;
        break;
      case "date":
        aValue = new Date(a.date).getTime();
        bValue = new Date(b.date).getTime();
        break;
      case "tourOperator":
        aValue = a.tourOperator;
        bValue = b.tourOperator;
        break;
      case "amount":
        aValue = a.amount;
        bValue = b.amount;
        break;
      case "createdByUserName":
        aValue = a.createdByUserName;
        bValue = b.createdByUserName;
        break;
      case "paymentMode":
        aValue = a.paymentMode;
        bValue = b.paymentMode;
        break;
      case "status":
        aValue = a.status;
        bValue = b.status;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const resetFilters = () => {
    setFilters({
      receiptNo: "",
      tourOperator: "",
      paymentMode: "all",
      dateFrom: "",
      dateTo: "",
      minAmount: "",
      maxAmount: "",
    });

    setSearchTerm("");
    setDebouncedSearch("");
    setCurrentPage(1);
    setSearchParams({ page: "1" });

    toast.success("Filters reset");
  };

  // Handle edit click
  const handleEditClick = (receipt: AdvanceReceipt) => {
    onNavigate(
      `/advance-receipts/edit/${receipt.id}?mode=${receipt.status === "POSTED" ? "view" : "edit"
      }&page=${currentPage}`
    );
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-4">
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />

              <Input
                placeholder="Search by Receipt No, Tour Operator, Office, or ITS Number..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="h-10 pl-9 pr-9 shadow-sm"
              />

              {searchTerm && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm("");
                    setCurrentPage(1);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowStatistics(!showStatistics)}
              className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10 shrink-0"
            >
              {showStatistics ? (
                <ArrowUp className="w-5 h-5" />
              ) : (
                <BarChart3 className="w-5 h-5" />
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`shrink-0 ${
                showFilters
                  ? "text-white"
                  : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"
              }`}
              title="Advanced Filters"
            >
              <Filter className="w-4 h-4 sm:mr-0" />
            </Button>
            {canAdd && (
              <Button
                onClick={() => onNavigate("/advance-receipts/create")}
                className="text-white shadow-lg hover:opacity-90 shrink-0"
                style={{ backgroundColor: "#5B9BD5" }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Receipt
              </Button>
            )}
          </div>
        </div>

        {/* Statistics Section - Collapsible */}
        {showStatistics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Draft Receipts</p>
                    <p className="text-2xl font-bold text-green-900">
                      {summary?.totalDraftReceipts ?? 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <FileStack className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-purple-200 bg-purple-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600">Draft Amount</p>
                    <p className="text-2xl font-bold text-purple-900">
                      ${summary?.totalDraftAmount?.toLocaleString() ?? "0"}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-600">Total Receipts</p>
                    <p className="text-2xl font-bold text-emerald-900">
                      {summary?.totalReceipts ?? 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Receipt className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600">Total Amount</p>
                    <p className="text-2xl font-bold text-blue-900">
                      ${summary?.totalAmount?.toLocaleString() ?? "0"}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Advanced Filters Section */}
        {showFilters && (
          <Card
            className="border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50"
            style={{ gap: "0px" }}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle
                    style={{ color: "#5B9BD5" }}
                    className="flex items-center gap-2 text-lg"
                  >
                    <Filter className="w-5 h-5" />
                    Advanced Filters
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Filter receipts by multiple criteria
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Receipt Number Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-receipt" className="text-sm">
                    Receipt Number
                  </Label>
                  <Input
                    id="filter-receipt"
                    placeholder="Search by receipt number..."
                    value={filters.receiptNo}
                    onChange={(e) =>
                      setFilters({ ...filters, receiptNo: e.target.value })
                    }
                    className="h-9 text-sm"
                  />
                </div>

                {/* Tour Operator Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-operator" className="text-sm">
                    Tour Operator
                  </Label>
                  <Input
                    id="filter-operator"
                    placeholder="Search by operator..."
                    value={filters.tourOperator}
                    onChange={(e) =>
                      setFilters({ ...filters, tourOperator: e.target.value })
                    }
                    className="h-9 text-sm"
                  />
                </div>

                {/* Payment Mode Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-payment" className="text-sm">
                    Payment Mode
                  </Label>
                  <Select
                    value={filters.paymentMode}
                    onValueChange={(value) =>
                      setFilters({ ...filters, paymentMode: value })
                    }
                  >
                    <SelectTrigger id="filter-payment" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Payment Modes</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Cheque">Cheque</SelectItem>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                      <SelectItem value="UPI">UPI</SelectItem>
                      <SelectItem value="Credit/Debit Card">
                        Credit/Debit Card
                      </SelectItem>
                      <SelectItem value="Refund">
                        Refund
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Date From Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-date-from" className="text-sm">
                    Date From
                  </Label>
                  <Input
                    id="filter-date-from"
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) =>
                      setFilters({ ...filters, dateFrom: e.target.value })
                    }
                    className="h-9 text-sm"
                  />
                </div>

                {/* Date To Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-date-to" className="text-sm">
                    Date To
                  </Label>
                  <Input
                    id="filter-date-to"
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) =>
                      setFilters({ ...filters, dateTo: e.target.value })
                    }
                    className="h-9 text-sm"
                  />
                </div>

                {/* Min Amount Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-min-amount" className="text-sm">
                    Min Amount ($)
                  </Label>
                  <Input
                    id="filter-min-amount"
                    type="number"
                    placeholder="Minimum..."
                    value={filters.minAmount}
                    onChange={(e) =>
                      setFilters({ ...filters, minAmount: e.target.value })
                    }
                    className="h-9 text-sm"
                  />
                </div>

                {/* Max Amount Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-max-amount" className="text-sm">
                    Max Amount ($)
                  </Label>
                  <Input
                    id="filter-max-amount"
                    type="number"
                    placeholder="Maximum..."
                    value={filters.maxAmount}
                    onChange={(e) =>
                      setFilters({ ...filters, maxAmount: e.target.value })
                    }
                    className="h-9 text-sm"
                  />
                </div>
              </div>

              {/* Active Filters Summary & Clear Button */}
              {(filters.receiptNo ||
                filters.tourOperator ||
                filters.paymentMode !== "all" ||
                filters.dateFrom ||
                filters.dateTo ||
                filters.minAmount ||
                filters.maxAmount) && (
                  <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#5B9BD5]/30 rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#5B9BD5] mb-2">
                        Active Filters:
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {filters.receiptNo && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            Receipt: {filters.receiptNo}
                          </Badge>
                        )}
                        {filters.tourOperator && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            Operator: {filters.tourOperator}
                          </Badge>
                        )}
                        {filters.paymentMode !== "all" && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            Payment: {filters.paymentMode}
                          </Badge>
                        )}
                        {filters.dateFrom && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            From: {new Date(filters.dateFrom).toLocaleDateString()}
                          </Badge>
                        )}
                        {filters.dateTo && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            To: {new Date(filters.dateTo).toLocaleDateString()}
                          </Badge>
                        )}
                        {filters.minAmount && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            Min: ${parseFloat(filters.minAmount).toLocaleString()}
                          </Badge>
                        )}
                        {filters.maxAmount && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            Max: ${parseFloat(filters.maxAmount).toLocaleString()}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={resetFilters}
                      className="border-red-300 text-red-600 hover:bg-red-50 flex-shrink-0"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear All
                    </Button>
                  </div>
                )}
            </CardContent>
          </Card>
        )}

        {/* Receipts Table */}
        <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px", marginTop: "10px" }}>
          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("receiptNo")}
                    >
                      <div className="flex items-center gap-1">
                        Receipt No
                        {sortColumn === "receiptNo" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("date")}
                    >
                      <div className="flex items-center gap-1">
                        Date
                        {sortColumn === "date" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("tourOperator")}
                    >
                      <div className="flex items-center gap-1">
                        Tour Operator
                        {sortColumn === "tourOperator" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Description
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("amount")}
                    >
                      <div className="flex items-center gap-1">
                        Amount
                        {sortColumn === "amount" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("createdByUserName")}
                    >
                      <div className="flex items-center gap-1">
                        Created By
                        {sortColumn === "createdByUserName" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("amount")}
                    >
                      <div className="flex items-center gap-1">Status</div>
                    </TableHead>
                    {(canEdit || canAdd || canApprove) && (
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs text-left"
                      >
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {receipts.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-8 px-6 text-gray-500"
                      >
                        No receipts found
                      </TableCell>
                    </TableRow>
                  ) : (
                    (sortColumn ? sortedReceipts : receipts).map((receipt) => (
                      <TableRow key={receipt.id} className="hover:bg-sky-50/30">
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                              <Receipt className="w-4 h-4 text-[#5B9BD5]" />
                            </div>
                            <div className="flex flex-col">
                              <span className="text-gray-900 text-sm">
                                {receipt.receiptNo}
                              </span>
                              <span className="text-xs text-gray-500">
                                {receipt.paymentMode}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-gray-700">
                            {new Date(receipt.date).toLocaleDateString()}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex flex-col">
                            {/* <span className="text-sm text-gray-900">
                              {receipt.tourOperator}
                            </span> */}
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {receipt.officeName}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-gray-700">
                            {receipt.description}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-gray-700">
                            ${receipt.amount.toLocaleString()}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-gray-700">
                            {receipt.createdByUserName}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-gray-700">
                            {receipt.status === "POSTED" && (
                              <Badge className="text-xs bg-green-100 text-green-700 border-green-300 px-2 py-0.5">
                                {receipt.status}
                              </Badge>
                            )}
                            {receipt.status === "DRAFT" && (
                              <Badge className="text-xs bg-blue-100 text-blue-700 border-blue-300 px-2 py-0.5">
                                {receipt.status}
                              </Badge>
                            )}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right">
                          <div className="flex items-center justify-start gap-1">
                            {/* Post Receipt */}
                            {canApprove && receipt.status === "DRAFT" ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handlePostReceipt(receipt)}
                                className="text-blue-600 hover:bg-blue-50 h-7 w-7 p-0"
                                title="Post Receipt"
                              >
                                <CheckCircle2 className="w-3.5 h-3.5 fill-blue-100 text-blue-600" />
                              </Button>
                            ) : (
                              /* Placeholder to preserve space */
                              <div className="h-7 w-7" />
                            )}

                            {/* Download */}
                            {canAdd && receipt.status === "POSTED" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDownloadClick(receipt)}
                                className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                title="Download receipt"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                            )}

                            {/* Edit */}
                            {canEdit && receipt.status === "DRAFT" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditClick(receipt)}
                                className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                title="Edit receipt"
                              // title={
                              //   receipt.status === "POSTED"
                              //     ? "View receipt"
                              //     : "Edit receipt"
                              // }
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-3 p-4">
              {receipts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No receipts found</p>
                </div>
              ) : (
                sortedReceipts.map((receipt) => (
                  <Card
                    key={receipt.id}
                    className="border-[#5B9BD5]/30 hover:shadow-md transition-shadow"
                  >
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3 flex-1">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                              <Receipt className="w-5 h-5 text-[#5B9BD5]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-gray-900 truncate">
                                {receipt.receiptNo}
                              </h3>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(receipt.date).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <Badge className="bg-[#5B9BD5] text-white">
                            ${receipt.amount.toLocaleString()}
                          </Badge>
                        </div>

                        {/* Details */}
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Tour Operator:</span>
                            <span className="font-medium text-gray-900">
                              {receipt.tourOperator}
                            </span>
                          </div>

                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Office:</span>
                            <span className="font-medium text-gray-900 flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {receipt.officeName}
                            </span>
                          </div>

                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Payment Mode:</span>
                            <Badge
                              variant="outline"
                              className="text-xs border-[#5B9BD5]/30 text-[#5B9BD5]"
                            >
                              {receipt.paymentMode}
                            </Badge>
                          </div>

                          <div>
                            <span className="text-gray-600 block mb-1">
                              Description:
                            </span>
                            <span className="text-xs text-gray-700">
                              {receipt.description}
                            </span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-2 border-t border-gray-100">
                          {receipt.status === "DRAFT" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handlePostReceipt(receipt)}
                              className="text-blue-600 hover:bg-blue-50 h-7 w-7 p-0"
                              title="Post Receipt"
                            >
                              <CheckCircle2 className="w-4 h-4 fill-blue-100 text-blue-600" />
                            </Button>
                          )}
                          {receipt.status === "POSTED" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadClick(receipt)}
                              className="flex-1 text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-sky-50"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download
                            </Button>
                          )}

                          {receipt.status === "DRAFT" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditClick(receipt)}
                              className="flex-1 text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-sky-50"
                            >
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={currentPage}
            onPageChange={(page) => {
              setCurrentPage(page);
              setSearchParams({ page: String(page) });
            }}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
              setSearchParams({ page: "1" });
            }}
            label="receipts"
          />
        </Card>

        <Dialog open={draftDialogOpen} onOpenChange={setDraftDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-600">
                <AlertCircle className="w-5 h-5" />
                Confirm change receipt to post.
              </DialogTitle>
              <DialogDescription className="text-gray-600 pt-2">
                Are you sure you want to post this draft? This action cannot be
                undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-4">
              <Button
                variant="outline"
                onClick={cancelDeleteDraft}
                className="border-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteDraft}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Post receipt
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
